import { useState, useEffect } from 'react';
import { Check, Plus, X, Calendar } from 'lucide-react';
import { CX_NIVELES } from '@/constants/cxNiveles';
import { fmt, deepClone } from '@/lib/utils';
import { calcularProyectoCX } from '@/lib/calc';
import FaseEditor from './FaseEditor';

const proyectoVacio = (nivel = 'base') => ({
  nombre: '',
  nivel,
  fase1: deepClone(CX_NIVELES[nivel].fase1.items),
  fase2: deepClone(CX_NIVELES[nivel].fase2.items),
  fase3: deepClone(CX_NIVELES[nivel].fase3.items),
  descuento3D: 0,
  descTipologias: false,
});

export default function CfgCX({ producto, onUpdate }) {
  const initCfg = producto.config?.proyectos
    ? producto.config
    : { variante: 'terminado', proyectos: [proyectoVacio('base')] };
  const [cfg, setCfg] = useState(initCfg);

  const recalc = (newCfg) => {
    const total = newCfg.proyectos.reduce((s, p) => s + calcularProyectoCX(p).total, 0);
    setCfg(newCfg);
    onUpdate({ config: newCfg, precioFinal: total });
  };

  useEffect(() => {
    if (!producto.configurado) recalc(cfg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aplicarNivel = (idx, nivel) => {
    const newProyectos = cfg.proyectos.map((p, i) =>
      i === idx
        ? {
            ...p,
            nivel,
            fase1: deepClone(CX_NIVELES[nivel].fase1.items),
            fase2: deepClone(CX_NIVELES[nivel].fase2.items),
            fase3: deepClone(CX_NIVELES[nivel].fase3.items),
          }
        : p
    );
    recalc({ ...cfg, proyectos: newProyectos });
  };

  const addProyecto = () => {
    recalc({ ...cfg, proyectos: [...cfg.proyectos, proyectoVacio('base')] });
  };

  const removeProyecto = (idx) => {
    if (cfg.proyectos.length === 1) return;
    recalc({ ...cfg, proyectos: cfg.proyectos.filter((_, i) => i !== idx) });
  };

  const updateProyecto = (idx, field, value) => {
    recalc({
      ...cfg,
      proyectos: cfg.proyectos.map((p, i) => (i === idx ? { ...p, [field]: value } : p)),
    });
  };

  const updateItem = (pidx, fase, iidx, field, value) => {
    recalc({
      ...cfg,
      proyectos: cfg.proyectos.map((p, i) =>
        i === pidx
          ? {
              ...p,
              [fase]: p[fase].map((item, j) =>
                j === iidx ? { ...item, [field]: value } : item
              ),
            }
          : p
      ),
    });
  };

  const addItem = (pidx, fase) => {
    recalc({
      ...cfg,
      proyectos: cfg.proyectos.map((p, i) =>
        i === pidx
          ? {
              ...p,
              [fase]: [
                ...p[fase],
                { id: `custom-${Date.now()}`, label: 'Servicio custom', precio: 0 },
              ],
            }
          : p
      ),
    });
  };

  const removeItem = (pidx, fase, iidx) => {
    recalc({
      ...cfg,
      proyectos: cfg.proyectos.map((p, i) =>
        i === pidx ? { ...p, [fase]: p[fase].filter((_, j) => j !== iidx) } : p
      ),
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="text-xs text-neutral-400 uppercase tracking-wider mb-2 block">
          Variante
        </label>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => recalc({ ...cfg, variante: 'preventa' })}
            className={`p-3 rounded-lg border text-sm ${
              cfg.variante === 'preventa'
                ? 'border-brand-gold bg-brand-gold/10 text-white'
                : 'border-neutral-800 bg-neutral-900/50 text-neutral-400'
            }`}
          >
            Preventa (3D / Renders)
          </button>
          <button
            onClick={() => recalc({ ...cfg, variante: 'terminado' })}
            className={`p-3 rounded-lg border text-sm ${
              cfg.variante === 'terminado'
                ? 'border-brand-gold bg-brand-gold/10 text-white'
                : 'border-neutral-800 bg-neutral-900/50 text-neutral-400'
            }`}
          >
            Proyecto Terminado (Drone/360°)
          </button>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-xs text-neutral-400 uppercase tracking-wider">
            Proyectos a desarrollar
          </label>
          <button
            onClick={addProyecto}
            className="text-xs text-brand-gold hover:text-yellow-400 flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> Agregar proyecto
          </button>
        </div>

        <div className="space-y-4">
          {cfg.proyectos.map((p, idx) => {
            const calc = calcularProyectoCX(p);
            return (
              <div
                key={idx}
                className="p-4 bg-neutral-900/70 rounded-lg border border-neutral-800"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs text-neutral-500">Proyecto {idx + 1}</div>
                  {cfg.proyectos.length > 1 && (
                    <button
                      onClick={() => removeProyecto(idx)}
                      className="text-neutral-500 hover:text-red-400"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>

                <input
                  type="text"
                  value={p.nombre}
                  onChange={(e) => updateProyecto(idx, 'nombre', e.target.value)}
                  placeholder="Nombre del proyecto o fase"
                  className="w-full px-3 py-2 bg-black/40 border border-neutral-800 rounded text-white text-sm mb-3 focus:outline-none focus:border-brand-gold/50"
                />

                <div className="mb-4">
                  <div className="text-[10px] text-neutral-500 uppercase tracking-wider mb-2">
                    Plantilla de nivel (aplica valores de referencia)
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(CX_NIVELES).map(([nivelId, nivel]) => (
                      <button
                        key={nivelId}
                        onClick={() => aplicarNivel(idx, nivelId)}
                        className={`p-2 rounded border text-xs ${
                          p.nivel === nivelId
                            ? 'border-brand-gold bg-brand-gold/10'
                            : 'border-neutral-800 bg-neutral-900/50 hover:border-neutral-700'
                        }`}
                      >
                        <div className="font-semibold text-white">{nivel.nombre}</div>
                        <div className="text-brand-gold">{fmt(nivel.total)}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <FaseEditor
                  titulo="Fase 1: Modelado y Levantamiento 3D"
                  items={p.fase1}
                  onUpdate={(ii, f, v) => updateItem(idx, 'fase1', ii, f, v)}
                  onAdd={() => addItem(idx, 'fase1')}
                  onRemove={(ii) => removeItem(idx, 'fase1', ii)}
                />

                <div className="mt-2 p-3 bg-black/30 rounded border border-neutral-900">
                  <label className="text-[10px] text-neutral-500 uppercase tracking-wider mb-1 flex items-center justify-between">
                    <span>Descuento por modelo 3D pre-existente</span>
                    <span className="text-brand-gold font-medium">{p.descuento3D}%</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="25"
                    value={p.descuento3D}
                    onChange={(e) =>
                      updateProyecto(idx, 'descuento3D', parseInt(e.target.value, 10))
                    }
                    className="w-full accent-brand-gold"
                  />
                  {p.descuento3D > 0 && (
                    <div className="text-xs text-green-400 mt-1">
                      Ahorro: {fmt(calc.desc3D)}
                    </div>
                  )}
                </div>

                <FaseEditor
                  titulo="Fase 2: Entregables a partir de 3D"
                  items={p.fase2}
                  onUpdate={(ii, f, v) => updateItem(idx, 'fase2', ii, f, v)}
                  onAdd={() => addItem(idx, 'fase2')}
                  onRemove={(ii) => removeItem(idx, 'fase2', ii)}
                />

                <label className="flex items-center gap-2 mt-2 p-3 bg-black/30 rounded border border-neutral-900 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={p.descTipologias}
                    onChange={(e) =>
                      updateProyecto(idx, 'descTipologias', e.target.checked)
                    }
                    className="accent-brand-gold"
                  />
                  <div className="flex-1">
                    <div className="text-xs text-white">
                      Descuento 10% en Fase 2 (aplica con 5+ tipologías)
                    </div>
                    {p.descTipologias && (
                      <div className="text-xs text-green-400">
                        Ahorro: {fmt(calc.descTip)}
                      </div>
                    )}
                  </div>
                </label>

                <FaseEditor
                  titulo="Fase 3: Programación e Implementación"
                  items={p.fase3}
                  onUpdate={(ii, f, v) => updateItem(idx, 'fase3', ii, f, v)}
                  onAdd={() => addItem(idx, 'fase3')}
                  onRemove={(ii) => removeItem(idx, 'fase3', ii)}
                />

                <div className="mt-3 p-3 bg-brand-gold/5 border border-brand-gold/20 rounded text-xs space-y-1">
                  <div className="flex justify-between text-neutral-400">
                    <span>Fase 1</span>
                    <span>
                      {fmt(calc.f1Total)}{' '}
                      {p.descuento3D > 0 && (
                        <span className="text-green-400">- {fmt(calc.desc3D)}</span>
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-neutral-400">
                    <span>Fase 2</span>
                    <span>
                      {fmt(calc.f2Total)}{' '}
                      {p.descTipologias && (
                        <span className="text-green-400">- {fmt(calc.descTip)}</span>
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-neutral-400">
                    <span>Fase 3</span>
                    <span>{fmt(calc.f3Total)}</span>
                  </div>
                  <div className="flex justify-between text-white font-semibold pt-1 border-t border-brand-gold/20">
                    <span>Total proyecto</span>
                    <span>{fmt(calc.total)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="p-3 bg-black/30 rounded text-xs text-neutral-400 border border-neutral-800">
        <div className="flex items-center gap-2">
          <Check className="w-3 h-3 text-green-500" /> Incluido: Implementación y capacitación
        </div>
        <div className="flex items-center gap-2">
          <Check className="w-3 h-3 text-green-500" /> Incluido: Licencia de plataforma años
          1-3
        </div>
        <div className="flex items-center gap-2 text-neutral-500">
          <Calendar className="w-3 h-3" /> A partir del año 4: renovación $33,000 MXN/año
        </div>
      </div>
    </div>
  );
}
